import { useEffect, useState } from 'react'
import { inDiscord, openExternal } from '../discord/sdk'
import { api, ApiError, imageUrl, type PickedRelease, type PickedTitle } from '../api'
import { formatSize, useI18n, type MessageKey, type Vars } from '../i18n'
import type { CatalogDetails, CatalogType, Release, ReleasesResult } from '../protocol'
import { Poster } from './Search'
import { PREFETCH, ReleaseTracksPanel, ReleaseTracksStrip } from './Tracks'

interface Props {
  type: CatalogType
  id: number
  token?: string
  /** What the "watch" button does with the release; may throw to show an error. */
  onChoose: (picked: PickedRelease, details: CatalogDetails) => Promise<void>
  actionLabel: string
  /** Chooses the title alone, without a release; may throw to show an error. */
  onSelect?: (picked: PickedTitle, details: CatalogDetails) => Promise<void>
  /** Season of a series to open on. */
  initialSeason?: number
  /** The label of the "choose" button and its hint. */
  selectLabel?: string
  selectHint?: string
}

/** A movie or series with the releases found for it. */
export function TitleView({ type, id, token, onChoose, actionLabel, onSelect, initialSeason, selectLabel, selectHint }: Props) {
  const { t, lang } = useI18n()
  const [details, setDetails] = useState<CatalogDetails | null>(null)
  const [error, setError] = useState('')
  const [season, setSeason] = useState(initialSeason ?? 0)
  const [selecting, setSelecting] = useState(false)
  const [selectError, setSelectError] = useState('')

  useEffect(() => {
    let cancelled = false
    setError('')
    api
      .catalogDetails(type, id, lang, token)
      .then((d) => {
        if (cancelled) return
        setDetails(d)
        // Series: start from the first season, which is what most people look for.
        setSeason((s) => (s === 0 && d.seasons?.length ? d.seasons[0].number : s))
      })
      .catch((e) => !cancelled && setError(e instanceof Error ? e.message : String(e)))
    return () => {
      cancelled = true
    }
  }, [type, id, lang, token])

  if (error) return <p className="error-text ctitle__error">{error}</p>
  if (!details) return <p className="muted ctitle__loading">{t('catalog.loading')}</p>

  const facts = [
    details.yearEnd && details.yearEnd !== details.year ? `${details.year}–${details.yearEnd}` : details.year,
    type === 'tv' && details.seasons?.length ? t('catalog.seasonsCount', { n: details.seasons.length }) : null,
    details.runtime ? t('catalog.minutes', { n: details.runtime }) : null,
    details.countries.slice(0, 2).map((c) => countryName(c, lang)).join(', ') || null,
  ].filter(Boolean)

  return (
    <article className="ctitle">
      <header className="ctitle__hero" style={details.backdrop ? { backgroundImage: `url(${imageUrl('w1280', details.backdrop)})` } : undefined}>
        <div className="ctitle__hero-inner">
          <Poster path={details.poster} size="w342" className="ctitle__poster" />
          <div className="ctitle__info">
            <p className="ctitle__kind">{t(type === 'tv' ? 'catalog.tv' : 'catalog.movie')}</p>
            <h2 className="ctitle__name">{details.title}</h2>
            {details.originalTitle !== details.title && <p className="ctitle__original">{details.originalTitle}</p>}
            <p className="ctitle__facts">{facts.join(', ')}</p>
            {details.genres.length > 0 && (
              <ul className="ctitle__genres">
                {details.genres.map((g) => (
                  <li key={g}>{g}</li>
                ))}
              </ul>
            )}
            {details.rating ? (
              <p className="ctitle__rating">
                <strong>{details.rating.toFixed(1)}</strong> <span className="muted">TMDb</span>
              </p>
            ) : null}
            {onSelect && (
              <div className="ctitle__select">
                <button
                  type="button"
                  className="btn btn--primary"
                  disabled={selecting}
                  onClick={async () => {
                    setSelecting(true)
                    setSelectError('')
                    try {
                      await onSelect({ type, id, season: type === 'tv' && season ? season : undefined }, details)
                    } catch (e) {
                      setSelectError(e instanceof Error ? e.message : String(e))
                    } finally {
                      setSelecting(false)
                    }
                  }}
                >
                  {selectLabel ?? t('catalog.select')}
                  {type === 'tv' && season ? ` · ${t('catalog.season', { n: season })}` : ''}
                </button>
                <span className="ctitle__select-hint">{selectHint ?? t('catalog.selectHint')}</span>
                {selectError && <p className="error-text">{selectError}</p>}
              </div>
            )}
          </div>
        </div>
      </header>
      {(details.tagline || details.overview) && (
        <section className="ctitle__about">
          {details.tagline && <p className="ctitle__tagline">{details.tagline}</p>}
          {details.overview && <p className="ctitle__overview">{details.overview}</p>}
        </section>
      )}
      {type === 'tv' && details.seasons && details.seasons.length > 1 && (
        <div className="ctitle__seasons" role="group" aria-label={t('catalog.seasonPick')}>
          {details.seasons.map((s) => (
            <button key={s.number} type="button" aria-pressed={season === s.number} onClick={() => setSeason(s.number)}>
              {t('catalog.season', { n: s.number })}
            </button>
          ))}
          <button type="button" aria-pressed={season === 0} onClick={() => setSeason(0)}>
            {t('catalog.allSeasons')}
          </button>
        </div>
      )}
      <Releases type={type} id={id} season={type === 'tv' ? season : 0} token={token} actionLabel={actionLabel} onChoose={(r) => onChoose({ release: { provider: r.provider, id: r.id }, catalog: { type, id, season: type === 'tv' ? season : undefined } }, details)} />
    </article>
  )
}

function Releases({
  type,
  id,
  season,
  token,
  actionLabel,
  onChoose,
}: {
  type: CatalogType
  id: number
  season: number
  token?: string
  actionLabel: string
  onChoose: (r: Release) => Promise<void>
}) {
  const { t } = useI18n()
  const [result, setResult] = useState<ReleasesResult | null>(null)
  const [error, setError] = useState('')
  const [busy, setBusy] = useState<string | null>(null)
  const [chooseError, setChooseError] = useState<{ id: string; message: string } | null>(null)
  const [open, setOpen] = useState<ReadonlySet<string>>(new Set())
  const toggle = (k: string) =>
    setOpen((s) => {
      const next = new Set(s)
      if (!next.delete(k)) next.add(k)
      return next
    })

  useEffect(() => {
    const ctrl = new AbortController()
    setResult(null)
    setError('')
    api
      .catalogReleases(type, id, season, token, ctrl.signal)
      .then(setResult)
      .catch((e) => !ctrl.signal.aborted && setError(e instanceof Error ? e.message : String(e)))
    return () => ctrl.abort()
  }, [type, id, season, token])

  const choose = async (r: Release) => {
    setBusy(releaseKey(r))
    setChooseError(null)
    try {
      await onChoose(r)
    } catch (e) {
      const message = e instanceof Error ? e.message : String(e)
      setChooseError({ id: releaseKey(r), message: sourceError(t, r.provider, e instanceof ApiError ? e.code : undefined, message) })
    } finally {
      setBusy(null)
    }
  }

  return (
    <section className="releases" aria-busy={!result && !error}>
      <h3 className="releases__title">{t('catalog.releases')}</h3>
      {!result && !error && (
        <p className="muted releases__note">
          <span className="releases__spinner" aria-hidden="true" /> {t('catalog.releasesLoading')}
        </p>
      )}
      {error && <p className="error-text">{error}</p>}
      {result?.errors &&
        Object.entries(result.errors).map(([p, e]) => (
          <p key={p} className={`${result.releases.length ? "muted" : "error-text"} releases__note`}>
            {sourceError(t, p, result.codes?.[p], `${p}: ${e}`)}
          </p>
        ))}
      {result && result.releases.length === 0 && !result.errors && <p className="muted releases__note">{t('catalog.noReleases')}</p>}
      {result && result.releases.length > 0 && (
        <ol className="releases__list">
          {result.releases.map((r, i) => (
            <li key={`${r.provider}${r.id}`} className="release">
              <div className="release__main">
                <Badges r={r} />
                <a
                  className="release__name"
                  href={r.url}
                  target="_blank"
                  rel="noreferrer noopener"
                  title={t('catalog.openTopic')}
                  onClick={(e) => {
                    // The activity iframe cannot open new windows itself.
                    if (!inDiscord) return
                    e.preventDefault()
                    openExternal(r.url)
                  }}
                >
                  {r.title}
                </a>
                <p className="release__meta muted">
                  {[r.category, r.provider].filter(Boolean).join(', ')}
                </p>
                <ReleaseTracksStrip release={r} season={season} token={token} prefetch={i < PREFETCH} open={open.has(releaseKey(r))} onToggle={() => toggle(releaseKey(r))} />
                {chooseError?.id === releaseKey(r) && <p className="error-text">{chooseError.message}</p>}
              </div>
              <div className="release__side">
                <span className="release__size">{formatSize(r.size)}</span>
                <span className="release__peers" title={t('catalog.peersTitle', { s: r.seeds, l: r.leeches })}>
                  <span className="release__seeds">↑ {r.seeds}</span> <span className="muted">↓ {r.leeches}</span>
                </span>
                <button type="button" className="btn btn--primary btn--small" disabled={busy !== null} onClick={() => void choose(r)}>
                  {busy === releaseKey(r) ? t('catalog.adding') : actionLabel}
                </button>
              </div>
              {open.has(releaseKey(r)) && <ReleaseTracksPanel release={r} season={season} token={token} />}
            </li>
          ))}
        </ol>
      )}
    </section>
  )
}

function Badges({ r }: { r: Release }) {
  const { t } = useI18n()
  const q = r.quality
  const badges: { text: string; tone?: 'accent' | 'warn' }[] = []
  if (q.resolution) badges.push({ text: q.resolution, tone: q.resolution === '2160p' ? 'accent' : undefined })
  if (q.dv) badges.push({ text: 'Dolby Vision' })
  else if (q.hdr) badges.push({ text: 'HDR' })
  if (q.codec) badges.push({ text: q.codec })
  if (q.source) badges.push({ text: q.disc ? t('catalog.disc') : q.source, tone: q.disc ? 'warn' : undefined })
  if (r.seasonFrom) {
    badges.push({
      text:
        r.seasonTo && r.seasonTo !== r.seasonFrom
          ? t('catalog.seasonsRange', { a: r.seasonFrom, b: r.seasonTo })
          : t('catalog.season', { n: r.seasonFrom }),
    })
  }
  if (r.episodes) badges.push({ text: t('catalog.episodes', { e: r.episodes }) })
  if (badges.length === 0) return null
  return (
    <ul className="badges">
      {badges.map((b) => (
        <li key={b.text} className={b.tone ? `badge badge--${b.tone}` : 'badge'}>
          {b.text}
        </li>
      ))}
    </ul>
  )
}

function countryName(code: string, lang: string): string {
  try {
    return new Intl.DisplayNames([lang], { type: 'region' }).of(code) ?? code
  } catch {
    return code
  }
}

const SOURCE_ERRORS: Record<string, MessageKey> = {
  blocked: 'catalog.sourceError.blocked',
  login: 'catalog.sourceError.login',
  captcha: 'catalog.sourceError.captcha',
}

/** Explains a source access failure in the interface language when the server gave a code. */
function sourceError(t: (key: MessageKey, vars?: Vars) => string, provider: string, code: string | undefined, fallback: string): string {
  const key = code ? SOURCE_ERRORS[code] : undefined
  return key ? t(key, { provider }) : fallback
}

const releaseKey = (r: Release) => `${r.provider}/${r.id}`
